import { useState, useEffect } from 'react';
import { Plus, Search, Eye, Edit, Trash2, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface Cargo {
  id: string;
  nome: string;
  descricao?: string;
  departamento?: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export default function Cargos() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState<Cargo | null>(null);
  const [sortColumn, setSortColumn] = useState<keyof Cargo>('nome');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  const [newCargo, setNewCargo] = useState({
    nome: '',
    descricao: '',
    departamento: ''
  });

  const departamentos = [
    'Administrativo',
    'Comercial', 
    'RH/DP',
    'Marketing',
    'Facilites',
    'Externo'
  ];

  useEffect(() => {
    fetchCargos();
  }, []);

  const fetchCargos = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('cargos')
        .select('*')
        .order('nome');

      if (error) throw error;
      setCargos(data as any[] || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar cargos",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCargo = async () => {
    if (!newCargo.nome.trim()) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Nome do cargo é obrigatório",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('cargos')
        .insert([{
          nome: newCargo.nome.trim(),
          descricao: newCargo.descricao.trim() || null,
          departamento: newCargo.departamento.trim() || null,
          created_by: user?.id
        }]);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Cargo criado com sucesso!",
      });

      setNewCargo({ nome: '', descricao: '', departamento: '' });
      setIsCreateDialogOpen(false);
      fetchCargos();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao criar cargo",
        description: error.message,
      });
    }
  };

  const handleUpdateCargo = async () => {
    if (!selectedCargo || !selectedCargo.nome.trim()) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Nome do cargo é obrigatório",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('cargos')
        .update({
          nome: selectedCargo.nome.trim(),
          descricao: selectedCargo.descricao?.trim() || null,
          departamento: selectedCargo.departamento?.trim() || null,
          updated_by: user?.id
        })
        .eq('id', selectedCargo.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Cargo atualizado com sucesso!",
      });

      setIsEditDialogOpen(false);
      setSelectedCargo(null);
      fetchCargos();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar cargo",
        description: error.message,
      });
    }
  };

  const handleDeleteCargo = async (id: string) => {
    try {
      const { error } = await supabase
        .from('cargos')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Cargo excluído com sucesso!",
      });

      fetchCargos();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao excluir cargo",
        description: error.message,
      });
    }
  };

  const handleSort = (column: keyof Cargo) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedCargos = cargos
    .filter(cargo =>
      cargo.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cargo.descricao && cargo.descricao.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (cargo.departamento && cargo.departamento.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortDirection === 'asc' 
        ? (aValue < bValue ? -1 : 1)
        : (aValue > bValue ? -1 : 1);
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg">Carregando cargos...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="h-6 w-6" />
            <CardTitle>Gestão de Cargos</CardTitle>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Criar Cargo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Novo Cargo</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="nome">Nome do Cargo *</Label>
                  <Input
                    id="nome"
                    value={newCargo.nome}
                    onChange={(e) => setNewCargo(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="Digite o nome do cargo"
                  />
                </div>
                <div>
                  <Label htmlFor="descricao">Descrição</Label>
                  <Textarea
                    id="descricao"
                    value={newCargo.descricao}
                    onChange={(e) => setNewCargo(prev => ({ ...prev, descricao: e.target.value }))}
                    placeholder="Digite a descrição do cargo"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="departamento">Departamento</Label>
                  <Select value={newCargo.departamento} onValueChange={(value) => setNewCargo(prev => ({ ...prev, departamento: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o departamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {departamentos.map((dept) => (
                        <SelectItem key={dept} value={dept}>
                          {dept}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleCreateCargo}>
                    Criar Cargo
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        
        <CardContent>
          <div className="mb-4 flex items-center gap-2">
            <Search className="h-4 w-4" />
            <Input
              placeholder="Buscar cargos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('nome')}
                  >
                    Nome {sortColumn === 'nome' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('descricao')}
                  >
                    Descrição {sortColumn === 'descricao' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('departamento')}
                  >
                    Departamento {sortColumn === 'departamento' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('created_at')}
                  >
                    Criado em {sortColumn === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                 {filteredAndSortedCargos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      Nenhum cargo encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedCargos.map((cargo) => (
                    <TableRow key={cargo.id}>
                      <TableCell className="font-medium">{cargo.nome}</TableCell>
                      <TableCell>{cargo.descricao || '-'}</TableCell>
                      <TableCell>{cargo.departamento || '-'}</TableCell>
                      <TableCell>{new Date(cargo.created_at).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCargo(cargo);
                              setIsViewDialogOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCargo(cargo);
                              setIsEditDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja excluir o cargo "{cargo.nome}"? Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteCargo(cargo.id)}>
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Visualizar Cargo</DialogTitle>
          </DialogHeader>
          {selectedCargo && (
            <div className="space-y-4">
              <div>
                <Label>Nome do Cargo</Label>
                <p className="mt-1 text-sm">{selectedCargo.nome}</p>
              </div>
              <div>
                <Label>Descrição</Label>
                <p className="mt-1 text-sm">{selectedCargo.descricao || 'Sem descrição'}</p>
              </div>
              <div>
                <Label>Departamento</Label>
                <p className="mt-1 text-sm">{selectedCargo.departamento || 'Sem departamento'}</p>
              </div>
              <div>
                <Label>Status</Label>
                <p className="mt-1 text-sm">{selectedCargo.status === 'ativo' ? 'Ativo' : 'Inativo'}</p>
              </div>
              <div>
                <Label>Criado em</Label>
                <p className="mt-1 text-sm">{new Date(selectedCargo.created_at).toLocaleString('pt-BR')}</p>
              </div>
              <div>
                <Label>Última atualização</Label>
                <p className="mt-1 text-sm">{new Date(selectedCargo.updated_at).toLocaleString('pt-BR')}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Cargo</DialogTitle>
          </DialogHeader>
          {selectedCargo && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-nome">Nome do Cargo *</Label>
                <Input
                  id="edit-nome"
                  value={selectedCargo.nome}
                  onChange={(e) => setSelectedCargo(prev => prev ? ({ ...prev, nome: e.target.value }) : null)}
                  placeholder="Digite o nome do cargo"
                />
              </div>
              <div>
                <Label htmlFor="edit-descricao">Descrição</Label>
                <Textarea
                  id="edit-descricao"
                  value={selectedCargo.descricao || ''}
                  onChange={(e) => setSelectedCargo(prev => prev ? ({ ...prev, descricao: e.target.value }) : null)}
                  placeholder="Digite a descrição do cargo"
                  rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-departamento">Departamento</Label>
                  <Select value={selectedCargo.departamento || ''} onValueChange={(value) => setSelectedCargo(prev => prev ? ({ ...prev, departamento: value }) : null)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o departamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {departamentos.map((dept) => (
                        <SelectItem key={dept} value={dept}>
                          {dept}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleUpdateCargo}>
                  Salvar Alterações
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}